// src/modules/PetSitter/requestConfig.ts
import { formatDate } from '@/utils/formatDate'

// --- Types ---
export type ServiceSlug = 'promenade' | 'garde-chez-vous' | 'garde-chez-eux' | 'visite'

export interface DateTimeInstance {
  date?: string
  dateStart?: string
  dateEnd?: string
  frequency?: 'once' | 'daily' | 'every_2_days' | 'every_3_days' | 'custom'
  customDays?: {
    scheduleEveryMonday: boolean
    scheduleEveryTuesday: boolean
    scheduleEveryWednesday: boolean
    scheduleEveryThursday: boolean
    scheduleEveryFriday: boolean
    scheduleEverySaturday: boolean
    scheduleEverySunday: boolean
  }
  timeStart?: string
  timeEnd?: string
  serviceDayCount?: number
  serviceDuration?: string
  timePeriods?: Array<{ timeStart?: string; timeEnd?: string; serviceDuration?: string }>
}

export interface ServiceValues {
  description: string
  animals: any[]
  dateTimeInstances: DateTimeInstance[]
  [key: string]: any
}

export type FormSlot = {
  id: string
  type: ServiceSlug
  data: DateTimeInstance | null
}

export interface Contact {
  id: string
  avatar: { imageUrl: string; imageThumbnailUrl: string }
  name: string
  checkbox: boolean
}

export interface CartProduct {
  offer: string
  start: string
  end: string
}

export interface CartData {
  products: CartProduct[]
  petsitters: string[]
  animals: string[]
  message: string
  rejectedPetsitters: {
    '@context': string
    '@id': string
    '@type': string
    petsitter: string
    reason: string
  }[]
}

// --- Constants ---
export const SERVICE_NAME_MAP = {
  'garde-chez-eux': 'Garde chez le pet sitter',
  'garde-chez-vous': 'Garde chez le propriétaire',
  promenade: 'Promenade(s)',
  visite: 'Visite(s) à domicile',
} as const

export const services = [
  {
    slug: 'garde-chez-eux',
    title: 'Garde chez le pet sitter',
    iconClass: 'emt-sit-home',
    priceLabel: 'À partir de 8 € / jour',
  },
  { slug: 'promenade', title: 'Promenade(s)', iconClass: 'emt-paws', priceLabel: 'À partir de 5 € / promenade' },
  {
    slug: 'visite',
    title: 'Visite(s) à domicile',
    iconClass: 'emt-search-property',
    priceLabel: 'À partir de 5 € / visite',
  },
  {
    slug: 'garde-chez-vous',
    title: 'Garde chez le propriétaire',
    iconClass: 'emt-sit',
    priceLabel: 'À partir de 9 € / jour',
  },
]

export const durationOptions = [
  { value: 'half_hour', label: '30 minutes' },
  { value: 'hour', label: '60 minutes' },
]

export const serviceSlugMap = {
  walk: 'promenade',
  sittingAtCustomerHome: 'garde-chez-vous',
  sittingAtPetsitterHome: 'garde-chez-eux',
  homeVisit: 'visite',
} as const

// --- Config ---
export const serviceConfig: Record<
  ServiceSlug,
  {
    formType: 'promenade' | 'garde' | 'visite'
    dateTimeFormTitle: string
    initialValues: any
    addButtonLabel: string
    dateTimeBoxTitle: (instance: DateTimeInstance) => string
    dateTimeBoxSubtitle: ((instance: DateTimeInstance) => string[] | null) | null
  }
> = {
  promenade: {
    formType: 'promenade',
    dateTimeFormTitle: 'Quelle date souhaitez-vous réserver ?',
    initialValues: {
      date: '',
      timePeriods: [{ timeStart: '', serviceDuration: '' }],
      dateStart: '',
      dateEnd: '',
      frequency: '',
      customDays: {
        scheduleEveryMonday: false,
        scheduleEveryTuesday: false,
        scheduleEveryWednesday: false,
        scheduleEveryThursday: false,
        scheduleEveryFriday: false,
        scheduleEverySaturday: false,
        scheduleEverySunday: false,
      },
      serviceDayCount: 1,
    },
    addButtonLabel: 'Ajouter une date',
    dateTimeBoxTitle: (instance) => formatDate(instance.date),
    dateTimeBoxSubtitle: (instance) =>
      instance.timePeriods?.map((period) => {
        const option = durationOptions.find((o) => o.value === period.serviceDuration)
        return `à ${period.timeStart} pour ${option ? option.label : period.serviceDuration}`
      }) ?? [],
  },
  'garde-chez-vous': {
    formType: 'garde',
    dateTimeFormTitle: 'Quelles dates souhaitez vous réserver ?',
    initialValues: { dateStart: '', timeStart: '', dateEnd: '', timeEnd: '' },
    addButtonLabel: 'Ajouter des dates',
    dateTimeBoxTitle: (instance) =>
      `Du ${formatDate(instance.dateStart!)} ${instance.timeStart ? 'à ' + instance.timeStart : ''} au ${formatDate(
        instance.dateEnd!,
      )} ${instance.timeEnd ? 'à ' + instance.timeEnd : ''}`,
    dateTimeBoxSubtitle: null,
  },
  'garde-chez-eux': {
    formType: 'garde',
    dateTimeFormTitle: 'Quelles dates souhaitez vous réserver ?',
    initialValues: { dateStart: '', timeStart: '', dateEnd: '', timeEnd: '' },
    addButtonLabel: 'Ajouter des dates',
    dateTimeBoxTitle: (instance) =>
      `Du ${formatDate(instance.dateStart!)} ${instance.timeStart ? 'à ' + instance.timeStart : ''} au ${formatDate(
        instance.dateEnd!,
      )} ${instance.timeEnd ? 'à ' + instance.timeEnd : ''}`,
    dateTimeBoxSubtitle: null,
  },
  visite: {
    formType: 'visite',
    dateTimeFormTitle: 'Quelle date souhaitez-vous réserver ?',
    initialValues: {
      date: '',
      timePeriods: [{ timeStart: '', serviceDuration: '' }],
      dateStart: '',
      dateEnd: '',
      frequency: '',
      customDays: {
        scheduleEveryMonday: false,
        scheduleEveryTuesday: false,
        scheduleEveryWednesday: false,
        scheduleEveryThursday: false,
        scheduleEveryFriday: false,
        scheduleEverySaturday: false,
        scheduleEverySunday: false,
      },
      serviceDayCount: 1,
    },
    addButtonLabel: 'Ajouter une date',
    dateTimeBoxTitle: (instance) => formatDate(instance.date),
    dateTimeBoxSubtitle: (instance) =>
      instance.timePeriods?.map((period) => {
        const option = durationOptions.find((o) => o.value === period.serviceDuration)
        return `à ${period.timeStart} pour ${option ? option.label : period.serviceDuration}`
      }) ?? [],
  },
}

// --- Helpers ---
export const uid = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : Math.random().toString(36).slice(2)

export const getSlotType = (selectedService?: string): ServiceSlug | null =>
  selectedService && ['promenade', 'garde-chez-vous', 'garde-chez-eux', 'visite'].includes(selectedService)
    ? (selectedService as ServiceSlug)
    : null

export const makeBlankSlot = (service: ServiceSlug, initialValues?: Partial<DateTimeInstance> | null): FormSlot => ({
  id: uid(),
  type: service,
  data: initialValues ? (initialValues as DateTimeInstance) : null,
})

export const checkHasService = (petsitter: any, selectedService?: string) => {
  if (!selectedService) return true
  switch (selectedService) {
    case 'promenade':
      return petsitter.walkPrice !== undefined && petsitter.walkPrice !== null
    case 'visite':
      return petsitter.homeVisitPrice !== undefined && petsitter.homeVisitPrice !== null
    case 'garde-chez-eux':
      return petsitter.sittingAtPetsitterHomePrice !== undefined && petsitter.sittingAtPetsitterHomePrice !== null
    case 'garde-chez-vous':
      return petsitter.sittingAtCustomerHomePrice !== undefined && petsitter.sittingAtCustomerHomePrice !== null
    default:
      return true
  }
}

export const isDateTimeInstanceIncomplete = (instance?: DateTimeInstance, serviceType?: string): boolean => {
  if (!instance) return true
  switch (serviceType) {
    case 'promenade':
      return !instance.date || !instance.timePeriods?.[0]?.timeStart || !instance.timePeriods?.[0]?.serviceDuration
    case 'garde-chez-vous':
    case 'garde-chez-eux':
      return !instance.dateStart || !instance.dateEnd || !instance.timeStart || !instance.timeEnd
    case 'visite':
      return !instance.date || !instance.timePeriods?.[0]?.timeStart || !instance.timePeriods?.[0]?.serviceDuration
    default:
      return false
  }
}

// --- Récurrence (promenade / visite) ---
export const MAX_RECURRENT_SLOTS = 30
export const MAX_RECURRENT_MONTHS = 3
export const MAX_SERVICE_DAY_COUNT = 4

export const RECURRENT_FREQUENCIES = ['once', 'daily', 'every_2_days', 'every_3_days', 'custom'] as const

export const WEEKDAY_FIELDS = [
  'scheduleEveryMonday',
  'scheduleEveryTuesday',
  'scheduleEveryWednesday',
  'scheduleEveryThursday',
  'scheduleEveryFriday',
  'scheduleEverySaturday',
  'scheduleEverySunday',
] as const

/**
 * Jours (YYYY-MM-DD, sans fuseau horaire) couverts par une promenade / visite selon sa fréquence.
 */
export const buildRecurrentDateList = (
  instance: Pick<DateTimeInstance, 'dateStart' | 'dateEnd' | 'frequency' | 'customDays'>,
): string[] => {
  if (!instance.dateStart) return []

  const parseYMD = (s: string) => {
    const [y, m, d] = s.split('-').map(Number)
    return new Date(y, m - 1, d)
  }
  const ymd = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n)
  const weekdayIndex = (d: Date) => (d.getDay() + 6) % 7 // Mon=0..Sun=6

  const start = parseYMD(instance.dateStart)
  const end = instance.frequency === 'once' ? start : parseYMD(instance.dateEnd || instance.dateStart)
  const list: string[] = []

  if (instance.frequency === 'once') {
    list.push(ymd(start))
  } else if (
    instance.frequency === 'daily' ||
    instance.frequency === 'every_2_days' ||
    instance.frequency === 'every_3_days'
  ) {
    const step = instance.frequency === 'daily' ? 1 : instance.frequency === 'every_2_days' ? 2 : 3
    for (let d = start; d.getTime() <= end.getTime(); d = addDays(d, step)) list.push(ymd(d))
  } else if (instance.frequency === 'custom' && instance.customDays) {
    const { customDays } = instance
    const active = WEEKDAY_FIELDS.map((field) => customDays[field])
    for (let d = start; d.getTime() <= end.getTime(); d = addDays(d, 1)) {
      if (active[weekdayIndex(d)]) list.push(ymd(d))
    }
  }

  return list
}
